import type { ReactNode } from "react";
import { AlertTriangle } from "./icons";
import { deltaText, initial } from "../lib/format";

/** หัวของหน้าจอคอนโซล (mockup `.admin-topline`) */
export function AdminTopline({
  title,
  subtitle,
  who,
  roleLabel,
  actions,
}: {
  title: string;
  subtitle?: string;
  who?: string;
  roleLabel?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="admin-topline">
      <div>
        <h1 className="admin-topline__title">{title}</h1>
        {subtitle ? <p className="admin-topline__subtitle">{subtitle}</p> : null}
      </div>
      <div className="row">
        {actions}
        {roleLabel ? (
          <span className="role-pill">
            <span className="avatar">{initial(who ?? roleLabel)}</span>
            {roleLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** กล่องเนื้อหา/กราฟ (mockup `.panel`) */
export function Panel({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <h2 className="panel__title">{title}</h2>
      {children}
      {note ? <p className="panel__note">{note}</p> : null}
    </section>
  );
}

/**
 * การ์ด KPI (mockup `.kpi`)
 *
 * `deltaPct` เป็น `null` แปลว่าไม่มีฐานให้เทียบ — แสดงเส้นประ ห้ามเดาเป็น 0%
 * `goodDirection` บอกว่าทิศไหนคือข่าวดี: คำร้องเพิ่ม = ดี แต่เวลารอเพิ่ม = ไม่ดี
 */
export function KpiCard({
  label,
  value,
  deltaPct,
  hint,
  goodDirection = "up",
}: {
  label: string;
  value: string;
  deltaPct?: number | null;
  hint?: string;
  goodDirection?: "up" | "down";
}) {
  const delta = deltaText(deltaPct);
  const isGood = delta ? delta.direction === goodDirection : false;
  return (
    <article className="kpi">
      <div className="lbl">{label}</div>
      <div className="val num">{value}</div>
      {delta ? (
        <div className={`delta ${isGood ? "up" : "warn"}`}>
          <span className="num">{delta.text}</span>
        </div>
      ) : (
        <div className="delta" style={{ color: "var(--ink-400)" }}>
          {hint ?? "— ບໍ່ມີຂໍ້ມູນໄລຍະກ່ອນໜ້າ"}
        </div>
      )}
    </article>
  );
}

/**
 * ป้ายบอกที่มาของตัวเลข
 *
 * จอนี้เป็นจอการเงินของราชการ ตัวเลขจะถูกเอาไปตัดสินใจจริง การบอกว่า "ยอดนี้คือเงินที่
 * ผู้ขอกดยืนยันเองว่าจ่ายแล้ว ยังไม่ตัดเงินจริง" (ADR-0015) จึงไม่ใช่ของประดับ
 */
export function ProvenanceNote({ children }: { children: ReactNode }) {
  return (
    <p className="provenance-note">
      <AlertTriangle size={14} />
      <span>{children}</span>
    </p>
  );
}
